import React, { useEffect, useState } from 'react'

import { Grid, TextField, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useFormContext } from 'react-hook-form'

import { FileUploadInput, Label, LoadingFeedback, Card } from 'components'

import useIncidentManegement from 'hooks/useIncidentManegement'
import useSnackbar from 'hooks/useSnackbar'
import usePermissions from 'hooks/usePermissions'

import * as service from 'service'
import { getBaseURL } from 'service/env'
import formatters from 'helpers/formatters'
import constants from 'constants/index'

const Form = ({ communicationIncidentEmail, attachment }) => {
  const [loading, setLoading] = useState(false)

  const snackbar = useSnackbar()
  const permissions = usePermissions()

  const {
    wantsToAdvanceStep,
    handleNext,
    resetAdvanceStep,
    incident,
    setAlreadySentCommunicationEmail,
    alreadySentCommunicationEmail,
    isShow,
  } = useIncidentManegement()

  const { handleSubmit } = useFormContext()

  const disabled =
    isShow || permissions.permitted(constants.permissions.INCIDENTS.CREATE)

  const attachmentFilename =
    communicationIncidentEmail?.attachmentFilename || attachment?.filename

  setAlreadySentCommunicationEmail(communicationIncidentEmail?.alreadySent)

  const documentTemplateUrl =
    getBaseURL('dponet') + '/modelo-lista-de-titulares.xlsx'

  const isRiskAssumed = incident.riskAssumptions?.communication?.riskAssumed

  const onSubmit = async () => {
    if (!attachment) return

    setLoading(true)

    try {
      await service.dponet.communicationIncidentEmail.upload({
        incidentId: incident.id,
        attachment: attachment,
      })

      snackbar.open({
        message: 'Arquivo anexado com sucesso',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao anexar o arquivo',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const onConcluded = async () => {
    if (disabled || isRiskAssumed) {
      resetAdvanceStep()
      handleNext()
      return
    }

    setLoading(true)

    try {
      await service.dponet.incidents.concludeStep({
        incidentId: incident?.id,
        step: constants.incidentManegement.COMMUNICATION_ENUM,
      })

      resetAdvanceStep()
      await handleNext()
    } catch (error) {
      snackbar.open({
        message: formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
      resetAdvanceStep()
    }
  }

  useEffect(() => {
    if (wantsToAdvanceStep && incident?.shouldNotify) {
      onConcluded()
      if (!disabled || !isRiskAssumed) onSubmit()
    }
  }, [wantsToAdvanceStep])

  return (
    <>
      <LoadingFeedback open={loading} />
      <Card title="Comunicação de titulares de dados">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Label title="Assunto do e-mail">
              <TextField
                fullWidth
                type="text"
                color="primary"
                variant="outlined"
                value={communicationIncidentEmail?.subject || ''}
                disabled
              />
            </Label>
          </Grid>
          <Grid item xs={12}>
            <Label
              title="Corpo do e-mail"
              description={
                'A flag {{titular}} indica o local onde o nome do titular de ' +
                'dados deverá ser inserido no corpo do e-mail. O sistema ' +
                'fará essa substituição automaticamente no momento do envio.'
              }
            >
              <TextField
                fullWidth
                multiline
                minRows={15}
                maxRows={15}
                type="text"
                color="primary"
                variant="outlined"
                value={communicationIncidentEmail?.content || ''}
                disabled
              />
            </Label>
          </Grid>
          {!alreadySentCommunicationEmail && (
            <Grid item xs={12}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Label
                  title='Anexar lista de titulares de dados (formato ".xlsx")'
                  useTooltip={false}
                  description={
                    <>
                      <Typography variant="caption">
                        O arquivo deve conter as colunas: nome (coluna A) e
                        e-mail (coluna B). iniciando a partir da linha 2.
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        Você pode baixar um modelo clicando{' '}
                        <a
                          href={documentTemplateUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          aqui
                        </a>
                        .
                      </Typography>
                    </>
                  }
                >
                  <FileUploadInput
                    accept=".xlsx"
                    title={
                      attachmentFilename
                        ? `Arquivo: ${attachmentFilename} anexado, arraste e solte ou clique para substituí-lo`
                        : 'Arraste e solte ou clique para selecionar o arquivo'
                    }
                    controlName="attachment"
                    disabled={disabled}
                  />
                </Label>
                {attachmentFilename && (
                  <Alert
                    severity="info"
                    variant="outlined"
                    style={{ marginTop: '16px' }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Clique em <strong>Avançar</strong> para efetivar o envio
                      da lista de titulares de dados.
                    </Typography>
                  </Alert>
                )}
              </form>
            </Grid>
          )}
        </Grid>
      </Card>
    </>
  )
}

export default Form
